// Persisted metals quote.
//
// Standard quotes (kind !== 'legacy'): the quoting page edits Draft quotes and
// can deliberately update a Released quote in place while preserving its number
// and Released status. The view also supports duplicating to a new Draft/number.
//
// Legacy quotes (kind === 'legacy'): paper transcriptions with scanned pages and
// explicit $/part breaks. Open on a read-only view; Edit always available (no
// draft/release lock). Obsolete is the lifecycle flag. releasedOn stores the
// date on the paper, not a lock time.
//
// The complex nested fields (sheetSizes, hardwareLines, processingLines,
// priceBreakLines, summary) are stored as JSON strings in Appwrite and
// serialized/deserialized in appwrite-document-conversion.function.ts.
//
// The part geometry is NOT stored — the original DXF is kept in storage
// (dxfFileId) and re-parsed when a draft is re-opened. `summary` is a cached
// snapshot so the browse/view pages render without re-nesting.

export enum MetalsQuoteStatus {
    Draft = 'draft',
    Released = 'released',
}

/** A hardware line item (qty × unit price). */
export interface MetalsQuoteHardwareLine {
    description: string;
    quantity: number;
    unitPrice: number;
    /** When true, this line is listed (description only, no price) on the exported quote PDF. */
    showOnQuote: boolean;
}

/** An additional-processing line item (named per-part cost). */
export interface MetalsQuoteProcessingLine {
    description: string;
    cost: number;
    /** When true, this line is listed (description only, no price) on the exported quote PDF. */
    showOnQuote: boolean;
}

/** How the quote entered the system: created in the quoting tool, or migrated from
 *  a legacy paper quote (photographed drawing with handwritten pricing). */
export type MetalsQuoteKind = 'standard' | 'legacy';

/** One photographed page of a legacy paper quote (stored in the metals-quote bucket). */
export interface MetalsQuoteScan {
    fileId: string;
    fileName: string;
    /** ISO timestamp of the phone upload. */
    uploadedOn: string;
    /** The representative page (browse thumbnail etc.). At most one scan per quote;
     *  when none is flagged, the first scan stands in. */
    primary?: boolean;
}

/** A PDF attached to the quote (stored in the metals-quote bucket). */
export interface MetalsQuoteAttachment {
    fileId: string;
    fileName: string;
    /** When true, this PDF's pages are appended to the exported quote PDF. */
    showOnQuote: boolean;
}

/** An editable price-break tier (quantity + unit-price multiplier). */
export interface MetalsQuotePriceBreakLine {
    quantity: number;
    multiplier: number;
}

/** A candidate sheet size (mirrors the quoting SheetSize; includes user-added customs). */
export interface MetalsQuoteSheetSize {
    id: string;
    label: string;
    widthIn: number;
    heightIn: number;
}

/** One computed price-break row in the cached summary. */
export interface MetalsQuoteBreak {
    quantity: number;
    multiplier: number;
    unitPrice: number;
    total: number;
}

/** One placed part on the selected sheet (lower-left of its rotated footprint, inches). */
export interface MetalsQuotePlacement {
    x: number;
    y: number;
    angleDeg: number;
}

/**
 * The exact nesting that was selected for the quote. Persisted as a companion JSON
 * file in storage (referenced by MetalsQuoteSummary.layoutFileId) — NOT re-nested on
 * view — so the released sheet always matches what was quoted, at any part count.
 */
export interface MetalsQuoteSheetLayout {
    sheetWidthIn: number;
    sheetHeightIn: number;
    partsPerSheet: number;
    placements: MetalsQuotePlacement[];
}

/** Cached pricing snapshot so browse/view can render without re-nesting. */
export interface MetalsQuoteSummary {
    unitPrice: number;
    sheetLabel: string;
    partsPerSheet: number;
    materialLabel: string;
    partWidthIn: number;
    partHeightIn: number;
    partAreaSqIn: number;
    breaks: MetalsQuoteBreak[];
    /** Storage file id of the selected MetalsQuoteSheetLayout (placements). */
    layoutFileId?: string;
}

export interface MetalsQuote {
    id: string;
    /** Monotonic, never-reused quote number (from a global-variable counter). */
    quoteNumber: number;
    status: MetalsQuoteStatus;
    /** Absent on rows created before legacy quotes existed — treat as 'standard'. */
    kind?: MetalsQuoteKind;
    /** Obsolete quotes are hidden from the browse page by default (still openable,
     *  and the mark is reversible). Orthogonal to draft/released status.
     *  NOTE: quote<->product linkage lives on MetalsProductVersion.quoteId (the version
     *  owns its quote, so the linkage is versioned). */
    obsolete?: boolean;
    /** Photographed pages of a legacy paper quote (empty for standard quotes). */
    scans?: MetalsQuoteScan[];

    // ---- identity ----
    customerId: string;
    /** Denormalized customer name so the browse list can sort/group without a join. */
    customerName: string;
    partNumber: string;
    description: string;
    preparedBy: string;
    /** Freeform quote notes (simple markdown: bullets / **bold** / *italic*) —
     *  rendered into the exported quote PDF's notes box. */
    notes: string;

    createdOn: Date;
    createdById: string;
    updatedOn: Date;
    updatedById: string;
    releasedOn: Date | null;

    // ---- source geometry (kept DXF; re-parsed on open) ----
    dxfFileId: string;
    dxfFileName: string;

    // ---- PDF attachments (files live in the metals-quote bucket) ----
    attachments: MetalsQuoteAttachment[];

    // ---- material ----
    materialFamily: string;
    materialId: string;
    pricePerLbOverride: number | null;
    materialCostMode: 'perLb' | 'perSheet';
    sheetCost: number | null;

    // ---- nesting config ----
    spacingIn: number;
    marginIn: number;
    /** Allowed rotation angles, comma-separated (e.g. "0,90,180,270"). */
    rotations: string;
    timeBudgetMs: number;
    sheetSizes: MetalsQuoteSheetSize[];
    selectedSheetId: string | null;

    // ---- pricing ----
    factor: number;
    roundUnitPrices: boolean;
    hardwareLines: MetalsQuoteHardwareLine[];
    processingLines: MetalsQuoteProcessingLine[];
    priceBreakLines: MetalsQuotePriceBreakLine[];
    autoMinQty: number;
    autoMaxQty: number;
    autoBreaks: number;
    autoRate: number;

    // ---- cached snapshot for browse/view ----
    summary: MetalsQuoteSummary | null;
}
